/**
 * Formatting Utilities
 *
 * Utility functions for formatting dates, numbers, and other data for display.
 */

import { format as dateFnsFormat, formatDistanceToNow, parseISO, subHours } from 'date-fns';

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Format date to locale string
 * @param date - ISO 8601 date string or Date object
 * @param formatString - date-fns format string (default: 'MMM dd, yyyy')
 */
export const formatDate = (date: string | Date, formatString: string = 'MMM dd, yyyy'): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return dateFnsFormat(dateObj, formatString);
  } catch (error) {
    console.error('[Format] Invalid date:', date, error);
    return 'Invalid date';
  }
};

/**
 * Format date to include time
 * @param date - ISO 8601 date string or Date object
 */
export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'MMM dd, yyyy HH:mm');
};

/**
 * Format date to relative time (e.g., "2 hours ago", "in 3 days")
 * @param date - ISO 8601 date string or Date object
 */
export const formatRelativeTime = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    console.error('[Format] Invalid date:', date, error);
    return 'Invalid date';
  }
};

/**
 * Format date to time only
 * @param date - ISO 8601 date string or Date object
 */
export const formatTime = (date: string | Date): string => {
  return formatDate(date, 'HH:mm:ss');
};

/**
 * Format timestamp for log display in UTC-03:00 timezone
 * @param timestamp - ISO 8601 timestamp string
 * @returns Formatted string like "2024-01-15 10:30:00.123 UTC-03"
 */
export const formatLogTimestamp = (timestamp: string): string => {
  try {
    const date = parseISO(timestamp);
    // CloudWatch returns timestamps in UTC, convert to UTC-03:00
    const utc3Date = subHours(date, 3);
    return `${dateFnsFormat(utc3Date, 'yyyy-MM-dd HH:mm:ss.SSS')} UTC-03`;
  } catch (error) {
    console.error('[Format] Invalid timestamp:', timestamp, error);
    return timestamp;
  }
};

// ============================================================================
// NUMBER FORMATTING
// ============================================================================

/**
 * Format number to locale string with optional decimal places
 * @param num - Number to format
 * @param decimals - Number of decimal places (default: 0)
 */
export const formatNumber = (num: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Format number as currency (USD)
 * @param num - Number to format
 */
export const formatCurrency = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

/**
 * Format number as percentage
 * @param num - Number to format (0-1 or 0-100)
 * @param isDecimal - If true, multiply by 100 (default: false)
 */
export const formatPercentage = (num: number, isDecimal: boolean = false): string => {
  const value = isDecimal ? num * 100 : num;
  return `${formatNumber(value, 1)}%`;
};

// ============================================================================
// BYTE FORMATTING
// ============================================================================

/**
 * Format bytes to human-readable size
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Convert GB to bytes
 * @param gb - Size in gigabytes
 */
export const gbToBytes = (gb: number): number => {
  return gb * 1024 * 1024 * 1024;
};

/**
 * Convert MB to bytes
 * @param mb - Size in megabytes
 */
export const mbToBytes = (mb: number): number => {
  return mb * 1024 * 1024;
};

/**
 * Convert bytes to GB
 * @param bytes - Size in bytes
 */
export const bytesToGb = (bytes: number): number => {
  return bytes / (1024 * 1024 * 1024);
};

/**
 * Convert bytes to MB
 * @param bytes - Size in bytes
 */
export const bytesToMb = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

// ============================================================================
// STRING FORMATTING
// ============================================================================

/**
 * Truncate text to specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 */
export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalize first letter of string
 * @param text - Text to capitalize
 */
export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Convert camelCase or snake_case to Title Case
 * @param text - Text to convert
 */
export const toTitleCase = (text: string): string => {
  return text
    .replace(/([A-Z])/g, ' $1') // camelCase to spaces
    .replace(/_/g, ' ') // snake_case to spaces
    .replace(/^./, (str) => str.toUpperCase()) // capitalize first letter
    .trim();
};

/**
 * Pluralize word based on count
 * @param count - Number of items
 * @param singular - Singular form
 * @param plural - Plural form (optional, defaults to singular + 's')
 */
export const pluralize = (count: number, singular: string, plural?: string): string => {
  const pluralForm = plural || `${singular}s`;
  return count === 1 ? singular : pluralForm;
};

// ============================================================================
// NAME FORMATTING
// ============================================================================

/**
 * Format full name from first and last name
 * @param firstName - First name
 * @param lastName - Last name
 */
export const formatFullName = (firstName: string, lastName: string): string => {
  return `${firstName} ${lastName}`.trim();
};

/**
 * Get initials from full name
 * @param firstName - First name
 * @param lastName - Last name (optional)
 */
export const getInitials = (firstName: string, lastName?: string): string => {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${first}${last}`;
};

// ============================================================================
// USER AGENT PARSING
// ============================================================================

/**
 * Parse user agent string to extract browser and OS info
 * @param userAgent - User agent string
 * @returns Object with browser name, OS, and device type
 */
export const parseUserAgent = (userAgent?: string): {
  browser: string;
  os: string;
  device: string;
} => {
  if (!userAgent) {
    return { browser: 'Unknown', os: 'Unknown', device: 'Unknown' };
  }

  const ua = userAgent.toLowerCase();

  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('edg/')) {
    browser = 'Edge';
  } else if (ua.includes('chrome') && !ua.includes('edg')) {
    browser = 'Chrome';
  } else if (ua.includes('firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('safari') && !ua.includes('chrome')) {
    browser = 'Safari';
  } else if (ua.includes('opera') || ua.includes('opr/')) {
    browser = 'Opera';
  } else if (ua.includes('msie') || ua.includes('trident')) {
    browser = 'Internet Explorer';
  }

  // Detect OS
  let os = 'Unknown';
  if (ua.includes('windows')) {
    os = 'Windows';
  } else if (ua.includes('mac os') || ua.includes('macos')) {
    os = 'macOS';
  } else if (ua.includes('linux') && !ua.includes('android')) {
    os = 'Linux';
  } else if (ua.includes('android')) {
    os = 'Android';
  } else if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ios')) {
    os = 'iOS';
  }

  // Detect device type
  let device = 'Desktop';
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    device = 'Mobile';
  } else if (ua.includes('tablet') || ua.includes('ipad')) {
    device = 'Tablet';
  }

  return { browser, os, device };
};

/**
 * Get a friendly device description from user agent
 * @param userAgent - User agent string
 * @returns Formatted string like "Chrome on macOS"
 */
export const getDeviceDescription = (userAgent?: string): string => {
  const { browser, os } = parseUserAgent(userAgent);
  if (browser === 'Unknown' && os === 'Unknown') {
    return 'Unknown device';
  }
  if (browser === 'Unknown') {
    return os;
  }
  if (os === 'Unknown') {
    return browser;
  }
  return `${browser} on ${os}`;
};
